/**
 * Dev-sync J1 — the psychrometric popup's localStorage contract.
 *
 * The legacy key: `psy_popup_state` ∈ 'open' | 'minimize' | 'close', written
 * with `fn.local_storage.set` semantics (a PLAIN string, no JSON quoting —
 * js_scripts/local_storage.js:9-14), byte-compatible with what the legacy
 * view wrote so a pre-migration value restores correctly:
 *  - 'open'     ← show()/restore() (psychrometric_chart.js:351/:416)
 *  - 'minimize' ← minimize() (:393)
 *  - 'close'    ← hide() (:382)
 *
 * Read at page load (header.js:250-266): ONLY the 'minimize' value does
 * anything — it rebuilds the popup SHELL (no data fetch) so the floating icon
 * reappears. 'open' does NOT auto-reopen the psychrometric dialog on reload
 * (unlike the Independent Rating popup's key) and there is NO cross-tab
 * storage listener for this key in legacy — both mirrored here by simply not
 * implementing either.
 *
 * Plus one key of our own: `psy_visible_streams`, the legend's air-stream
 * checkboxes. No legacy counterpart, so it is plain JSON rather than the bare
 * string above. Per browser, not per AHU — it is a viewing preference, like
 * which season tab you left the sidebar on, not a property of the unit.
 */

import {
  normalizeStreamVisibility,
  type PsyStreamVisibility,
} from './streamVisibility';

export type PsyPopupState = 'open' | 'minimize' | 'close';

export const PSY_POPUP_STATE_KEY = 'psy_popup_state';

export function getPsyPopupState(): PsyPopupState | null {
  try {
    const raw = window.localStorage.getItem(PSY_POPUP_STATE_KEY);
    return raw === 'open' || raw === 'minimize' || raw === 'close' ? raw : null;
  } catch {
    return null;
  }
}

export function setPsyPopupState(state: PsyPopupState): void {
  try {
    window.localStorage.setItem(PSY_POPUP_STATE_KEY, state);
  } catch {
    /* quota / private mode — nothing depends on the write succeeding */
  }
}

export const PSY_VISIBLE_STREAMS_KEY = 'psy_visible_streams';

/** The stored legend checkboxes, or all-visible. Unparseable JSON and a
 * partial object both fall back through normalizeStreamVisibility, so a bad
 * value can only ever show MORE of the chart, never less. */
export function getPsyStreamVisibility(): PsyStreamVisibility {
  try {
    const raw = window.localStorage.getItem(PSY_VISIBLE_STREAMS_KEY);
    return normalizeStreamVisibility(raw ? JSON.parse(raw) : null);
  } catch {
    return normalizeStreamVisibility(null);
  }
}

export function setPsyStreamVisibility(visibility: PsyStreamVisibility): void {
  try {
    window.localStorage.setItem(PSY_VISIBLE_STREAMS_KEY, JSON.stringify(visibility));
  } catch {
    /* quota / private mode — the checkboxes still work for this session */
  }
}
